import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, FilterBar, MetricCard, Modal, PageHeader, StatusBadge } from '../components/ui';
import { mapStatusToBadge } from '../lib/status';
import {
  adminWorkflowsService,
  type AssignableUser,
  type RoleAssignmentUser,
  type RolePermission,
  type RoleProfile,
  type RoleStatus,
} from '../services/admin-workflows.service';

type ViewMode = 'grid' | 'list';
type SortMode = 'name' | 'members' | 'updated';
type PermissionAction = 'read' | 'create' | 'update' | 'approve' | 'export';

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const countApprovedDomains = (permissions: RolePermission[]) =>
  permissions.filter((item) => item.approve).length;

const matchesUserQuery = (
  user: Pick<AssignableUser, 'fullName' | 'email' | 'status'>,
  normalizedQuery: string
) =>
  normalizedQuery.length === 0 ||
  user.fullName.toLowerCase().includes(normalizedQuery) ||
  user.email.toLowerCase().includes(normalizedQuery) ||
  user.status.toLowerCase().includes(normalizedQuery);

export default function AdminRolesPermissionsPage() {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<RoleProfile[]>([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | RoleStatus>('all');
  const [sortBy, setSortBy] = useState<SortMode>('updated');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const [activeRole, setActiveRole] = useState<RoleProfile | null>(null);
  const [draftPermissions, setDraftPermissions] = useState<RolePermission[]>([]);

  const [membershipRole, setMembershipRole] = useState<RoleProfile | null>(null);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [assignedUsers, setAssignedUsers] = useState<RoleAssignmentUser[]>([]);
  const [membersQuery, setMembersQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersActionLoading, setMembersActionLoading] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadRoles = async () => {
      try {
        setLoading(true);
        const data = await adminWorkflowsService.getRoles({
          sortBy: 'updated',
        });

        if (!active) {
          return;
        }

        setRoles(data);
        setError(null);
      } catch (loadError) {
        if (!active) {
          return;
        }

        setError((loadError as Error).message || 'Failed to load roles and permissions');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadRoles();

    return () => {
      active = false;
    };
  }, []);

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'All', count: roles.length },
      { value: 'active', label: 'Active', count: roles.filter((item) => item.status === 'active').length },
      { value: 'suspicious', label: 'Suspicious', count: roles.filter((item) => item.status === 'suspicious').length },
      { value: 'disabled', label: 'Disabled', count: roles.filter((item) => item.status === 'disabled').length },
    ],
    [roles]
  );

  const visibleRoles = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filtered = roles.filter((item) => {
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const matchesQuery =
        normalizedQuery === '' ||
        item.name.toLowerCase().includes(normalizedQuery) ||
        item.scope.toLowerCase().includes(normalizedQuery) ||
        item.permissions.some((permission) => permission.domain.toLowerCase().includes(normalizedQuery));
      return matchesStatus && matchesQuery;
    });

    return filtered.sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      }
      if (sortBy === 'members') {
        return b.assignedUsers - a.assignedUsers;
      }
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [query, roles, sortBy, statusFilter]);

  const metricSummary = useMemo(
    () => ({
      activeRoles: roles.filter((item) => item.status === 'active').length,
      suspendedRoles: roles.filter((item) => item.status === 'disabled').length,
      assignedUsers: roles.reduce((sum, item) => sum + item.assignedUsers, 0),
      privilegedRoles: roles.filter((item) => countApprovedDomains(item.permissions) > 0).length,
    }),
    [roles]
  );

  const openRoleEditor = (role: RoleProfile) => {
    setActiveRole(role);
    setDraftPermissions(role.permissions.map((permission) => ({ ...permission })));
  };

  const closeRoleEditor = () => {
    setActiveRole(null);
    setDraftPermissions([]);
  };

  const togglePermission = (domain: string, action: PermissionAction) => {
    setDraftPermissions((prev) =>
      prev.map((permission) =>
        permission.domain === domain ? { ...permission, [action]: !permission[action] } : permission
      )
    );
  };

  const savePermissionChanges = async () => {
    if (!activeRole) {
      return;
    }

    try {
      setActionLoading(`permissions-${activeRole.id}`);
      const updated = await adminWorkflowsService.updateRolePermissions(activeRole.id, draftPermissions);
      setRoles((prev) => prev.map((role) => (role.id === updated.id ? updated : role)));
      closeRoleEditor();
      setSuccess('Role permission matrix updated.');
      setError(null);
    } catch (actionError) {
      setError((actionError as Error).message || 'Failed to update permission matrix');
    } finally {
      setActionLoading(null);
    }
  };

  const setRoleStatus = async (roleId: string, status: RoleStatus) => {
    try {
      setActionLoading(`status-${roleId}`);
      const updated = await adminWorkflowsService.updateRoleStatus(roleId, status);
      setRoles((prev) => prev.map((role) => (role.id === updated.id ? updated : role)));
      setSuccess(`Role status changed to ${status}.`);
      setError(null);
    } catch (actionError) {
      setError((actionError as Error).message || 'Failed to update role status');
    } finally {
      setActionLoading(null);
    }
  };

  const openMembershipManager = async (role: RoleProfile) => {
    setMembershipRole(role);
    setMembersLoading(true);
    setSelectedUserIds([]);
    setMembersQuery('');

    try {
      const [roleUsers, users] = await Promise.all([
        adminWorkflowsService.getRoleUsers(role.id),
        adminWorkflowsService.getAssignableUsers(),
      ]);
      setAssignedUsers(roleUsers);
      setAssignableUsers(users);
      setError(null);
    } catch (membershipError) {
      setError((membershipError as Error).message || 'Failed to load role memberships');
    } finally {
      setMembersLoading(false);
    }
  };

  const closeMembershipManager = () => {
    setMembershipRole(null);
    setAssignedUsers([]);
    setAssignableUsers([]);
    setMembersQuery('');
    setSelectedUserIds([]);
    setMembersActionLoading(null);
  };

  const assignedUserIds = useMemo(() => new Set(assignedUsers.map((user) => user.id)), [assignedUsers]);
  const normalizedMembersQuery = membersQuery.trim().toLowerCase();

  const visibleAssignedUsers = useMemo(
    () =>
      assignedUsers.filter((user) => matchesUserQuery(user, normalizedMembersQuery)).sort((a, b) => {
        if (a.assignedAt === b.assignedAt) {
          return a.fullName.localeCompare(b.fullName);
        }
        return new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime();
      }),
    [assignedUsers, normalizedMembersQuery]
  );

  const visibleAssignableUsers = useMemo(
    () =>
      assignableUsers
        .filter((user) => !assignedUserIds.has(user.id))
        .filter((user) => matchesUserQuery(user, normalizedMembersQuery))
        .sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [assignableUsers, assignedUserIds, normalizedMembersQuery]
  );

  const toggleSelectedUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((selectedId) => selectedId !== userId) : [...prev, userId]
    );
  };

  const assignSelectedUsers = async () => {
    if (!membershipRole || selectedUserIds.length === 0) {
      return;
    }

    const toAssign = [...selectedUserIds];

    try {
      setMembersActionLoading('assign');
      const updatedRole = await adminWorkflowsService.assignUsersToRole(membershipRole.id, toAssign);
      const [roleUsers, users] = await Promise.all([
        adminWorkflowsService.getRoleUsers(membershipRole.id),
        adminWorkflowsService.getAssignableUsers(),
      ]);

      setAssignedUsers(roleUsers);
      setAssignableUsers(users);
      setSelectedUserIds([]);

      if (updatedRole) {
        setRoles((prev) => prev.map((role) => (role.id === updatedRole.id ? updatedRole : role)));
        setMembershipRole((current) => (current && current.id === updatedRole.id ? updatedRole : current));
      }

      setSuccess(
        `${toAssign.length} user${toAssign.length === 1 ? '' : 's'} assigned to ${membershipRole.name}.`
      );
      setError(null);
    } catch (assignmentError) {
      setError((assignmentError as Error).message || 'Failed to assign users to role');
    } finally {
      setMembersActionLoading(null);
    }
  };

  const removeRoleUser = async (user: RoleAssignmentUser) => {
    if (!membershipRole) {
      return;
    }

    try {
      setMembersActionLoading(`remove-${user.id}`);
      const updatedRole = await adminWorkflowsService.removeUserFromRole(membershipRole.id, user.id);
      const [roleUsers, users] = await Promise.all([
        adminWorkflowsService.getRoleUsers(membershipRole.id),
        adminWorkflowsService.getAssignableUsers(),
      ]);

      setAssignedUsers(roleUsers);
      setAssignableUsers(users);
      setSelectedUserIds((prev) => prev.filter((selectedId) => selectedId !== user.id));

      if (updatedRole) {
        setRoles((prev) => prev.map((role) => (role.id === updatedRole.id ? updatedRole : role)));
        setMembershipRole((current) => (current && current.id === updatedRole.id ? updatedRole : current));
      }

      setSuccess(`${user.fullName} removed from ${membershipRole.name}.`);
      setError(null);
    } catch (removeError) {
      setError((removeError as Error).message || 'Failed to remove user from role');
    } finally {
      setMembersActionLoading(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <PageHeader
        title="Roles & Permissions"
        description="Persisted RBAC workflow with role inventory, matrix editing, and membership assignment."
        badge={<StatusBadge tone="active">Persisted Workflow</StatusBadge>}
        actions={
          <Button variant="outline" onClick={() => navigate('/admin')}>
            Back
          </Button>
        }
      />

      {success && <Alert variant="success">{success}</Alert>}
      {error && <Alert variant="error">{error}</Alert>}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard title="Active Roles" value={metricSummary.activeRoles} tone="green" icon="A" />
        <MetricCard title="Suspended Roles" value={metricSummary.suspendedRoles} tone="red" icon="S" />
        <MetricCard title="Assigned Users" value={metricSummary.assignedUsers} tone="blue" icon="U" />
        <MetricCard title="Privileged Roles" value={metricSummary.privilegedRoles} tone="purple" icon="P" />
      </section>

      <FilterBar
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="Search role, scope, or domain..."
        statusOptions={statusOptions}
        selectedStatus={statusFilter}
        onStatusChange={(value) => setStatusFilter(value as 'all' | RoleStatus)}
        sortOptions={[
          { value: 'updated', label: 'Sort: Last Updated' },
          { value: 'name', label: 'Sort: Name A-Z' },
          { value: 'members', label: 'Sort: Assigned Users' },
        ]}
        selectedSort={sortBy}
        onSortChange={(value) => setSortBy(value as SortMode)}
        viewOptions={[
          { value: 'grid', label: 'View: Grid' },
          { value: 'list', label: 'View: List' },
        ]}
        selectedView={viewMode}
        onViewChange={(value) => setViewMode(value as ViewMode)}
      />

      {loading ? (
        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm text-sm text-gray-600">
          Loading RBAC roles...
        </section>
      ) : viewMode === 'grid' ? (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleRoles.map((role) => {
            const statusBadge = mapStatusToBadge(role.status);
            return (
              <article key={role.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-base font-semibold text-gray-900">{role.name}</h2>
                  <StatusBadge tone={statusBadge.tone} size="sm">
                    {statusBadge.label}
                  </StatusBadge>
                </div>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-xs font-semibold uppercase text-gray-500">Scope</dt>
                    <dd className="text-gray-900">{role.scope}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase text-gray-500">Assigned Users</dt>
                    <dd className="text-gray-900">{role.assignedUsers}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-xs font-semibold uppercase text-gray-500">Last Updated</dt>
                    <dd className="text-gray-900">{formatDateTime(role.updatedAt)}</dd>
                  </div>
                </dl>
                <div className="text-xs text-gray-600">
                  {countApprovedDomains(role.permissions)} domains with approve permissions.
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openMembershipManager(role)}
                    disabled={actionLoading !== null}
                  >
                    Manage Users
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openRoleEditor(role)}
                    disabled={actionLoading !== null}
                  >
                    Edit Matrix
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRoleStatus(role.id, 'active')}
                    disabled={actionLoading !== null}
                  >
                    Activate
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => setRoleStatus(role.id, 'disabled')}
                    disabled={actionLoading !== null}
                  >
                    Suspend
                  </Button>
                </div>
              </article>
            );
          })}
        </section>
      ) : (
        <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" aria-label="Roles list">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    Role
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    Scope
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    Users
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    Updated
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {visibleRoles.map((role) => {
                  const statusBadge = mapStatusToBadge(role.status);
                  return (
                    <tr key={role.id}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{role.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{role.scope}</td>
                      <td className="px-4 py-3">
                        <StatusBadge tone={statusBadge.tone} size="sm">
                          {statusBadge.label}
                        </StatusBadge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{role.assignedUsers}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatDateTime(role.updatedAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openMembershipManager(role)}
                            disabled={actionLoading !== null}
                          >
                            Users
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openRoleEditor(role)}
                            disabled={actionLoading !== null}
                          >
                            Matrix
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setRoleStatus(role.id, 'active')}
                            disabled={actionLoading !== null}
                          >
                            Activate
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => setRoleStatus(role.id, 'disabled')}
                            disabled={actionLoading !== null}
                          >
                            Suspend
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <Modal isOpen={activeRole !== null} onClose={closeRoleEditor} title={activeRole ? `Permission Matrix: ${activeRole.name}` : 'Permission Matrix'} size="lg">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Toggle domain permissions and save to apply policy updates.</p>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200" aria-label="Role permission matrix">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    Domain
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    Read
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    Create
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    Update
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    Approve
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    Export
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {draftPermissions.map((permission) => (
                  <tr key={permission.domain}>
                    <td className="px-3 py-2 text-sm font-medium text-gray-900">{permission.domain}</td>
                    {(['read', 'create', 'update', 'approve', 'export'] as PermissionAction[]).map((action) => (
                      <td key={`${permission.domain}-${action}`} className="px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={permission[action]}
                          onChange={() => togglePermission(permission.domain, action)}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600"
                          aria-label={`${permission.domain} ${action} permission`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={closeRoleEditor}>
              Cancel
            </Button>
            <Button onClick={savePermissionChanges} disabled={actionLoading !== null}>
              Save Matrix
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={membershipRole !== null}
        onClose={closeMembershipManager}
        title={membershipRole ? `Role Membership: ${membershipRole.name}` : 'Role Membership'}
        size="xl"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Assign or remove users for this role. Membership updates are persisted immediately.
          </p>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Search users</span>
            <input
              type="search"
              value={membersQuery}
              onChange={(event) => setMembersQuery(event.target.value)}
              placeholder="Search by user name, email, or status..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
            />
          </label>

          {membersLoading ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              Loading role users...
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  Assigned Users ({visibleAssignedUsers.length})
                </h3>
                {visibleAssignedUsers.length === 0 ? (
                  <p className="mt-3 text-sm text-gray-500">No assigned users match this search.</p>
                ) : (
                  <div className="mt-3 max-h-72 space-y-2 overflow-y-auto">
                    {visibleAssignedUsers.map((user) => (
                      <article
                        key={user.id}
                        className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium text-gray-900">{user.fullName}</p>
                            <p className="text-xs text-gray-600">{user.email}</p>
                            <p className="mt-1 text-[11px] text-gray-500">
                              Assigned {formatDateTime(user.assignedAt)}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => removeRoleUser(user)}
                            disabled={membersActionLoading !== null}
                          >
                            {membersActionLoading === `remove-${user.id}` ? 'Removing...' : 'Remove'}
                          </Button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>

              <section className="rounded-lg border border-gray-200 bg-white p-4">
                <h3 className="text-sm font-semibold text-gray-900">
                  Available Users ({visibleAssignableUsers.length})
                </h3>
                {visibleAssignableUsers.length === 0 ? (
                  <p className="mt-3 text-sm text-gray-500">No available users match this search.</p>
                ) : (
                  <div className="mt-3 max-h-72 space-y-2 overflow-y-auto">
                    {visibleAssignableUsers.map((user) => (
                      <label
                        key={user.id}
                        className="flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-3"
                      >
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => toggleSelectedUser(user.id)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600"
                          aria-label={`Assign ${user.fullName} to role`}
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{user.fullName}</p>
                          <p className="text-xs text-gray-600">{user.email}</p>
                          <p className="mt-1 text-[11px] uppercase tracking-wide text-gray-500">{user.status}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}

          <div className="flex justify-between gap-2">
            <p className="text-xs text-gray-500">
              Selected for assignment: {selectedUserIds.length}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={closeMembershipManager}>
                Close
              </Button>
              <Button
                onClick={assignSelectedUsers}
                disabled={selectedUserIds.length === 0 || membersActionLoading !== null}
              >
                {membersActionLoading === 'assign' ? 'Assigning...' : 'Assign Selected'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}

